import { useTranslate } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import type { ExtrasCambioEtapa } from "../datos/dataProvider";
import { useAjustes } from "../datos/useAjustes";
import { claveEtapa, type DatoDeEtapa } from "../dominio/etapas";
import { aFechaISO, sumarDias } from "../dominio/fechas";
import type { Etapa } from "../dominio/tipos";
import { deDatetimeLocal } from "./formato";
import { SelectorHuecos } from "./SelectorHuecos";

/**
 * Pide la fecha que necesita una etapa al moverla a mano (arrastrar en el tablero o menú de
 * la ficha): consultoría y presentación con fecha y hora (salen en la agenda) y Largo plazo
 * con fecha de vuelta; 39, «Aceptada» con el importe aceptado; 47, «No interesado» y «Largo
 * plazo» con su motivo (`motivos`, la lista que toca según el momento). Cancelar deja la tarjeta
 * donde estaba.
 */
export const MoverEtapaDialog = ({
  nombre,
  destino,
  dato,
  motivos,
  importeActual,
  onCancelar,
  onMover,
}: {
  nombre: string;
  destino: Etapa | null;
  dato: Exclude<DatoDeEtapa, "cobro"> | null;
  /** 47: si llega, hay que elegir un motivo de esta lista. */
  motivos?: string[] | null;
  /** 39: el importe que ya tenga (o el valor estimado), para proponerlo al pasar a «Aceptada». */
  importeActual?: number | null;
  onCancelar: () => void;
  onMover: (extras: ExtrasCambioEtapa, motivo?: string) => void;
}) => {
  const translate = useTranslate();
  const { ajustes } = useAjustes();
  const [valor, setValor] = useState("");
  const [error, setError] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [sinMotivo, setSinMotivo] = useState(false);
  // 41: con el calendario propio, la consultoría se agenda en un hueco libre (la base elige quién).
  const conHuecos = dato === "fecha_reunion" && ajustes.agenda.propia;

  useEffect(() => {
    setValor(
      dato === "fecha_vuelta"
        ? aFechaISO(sumarDias(new Date(), 90))
        : dato === "importe_total" && importeActual != null
          ? String(importeActual)
          : "",
    );
    setError(false);
    setMotivo("");
    setSinMotivo(false);
  }, [destino, dato, importeActual]);

  const esImporte = dato === "importe_total";

  const mover = () => {
    if (motivos && !motivo) {
      setSinMotivo(true);
      return;
    }
    if (!dato) {
      if (motivos) onMover({}, motivo);
      return;
    }
    if (conHuecos) {
      if (!valor) {
        setError(true);
        return;
      }
      onMover({ fecha_reunion: valor });
      return;
    }
    if (esImporte) {
      const importe = Number(valor.replace(",", "."));
      if (!(importe > 0)) {
        setError(true);
        return;
      }
      onMover({ importe_total: importe });
      return;
    }
    const fecha = dato === "fecha_vuelta" ? valor : deDatetimeLocal(valor);
    if (!fecha) {
      setError(true);
      return;
    }
    onMover({ [dato]: fecha }, motivos ? motivo : undefined);
  };

  return (
    <Dialog
      open={!!destino && (!!dato || !!motivos)}
      onOpenChange={(abierto) => !abierto && onCancelar()}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="titulo-seccion">
            {destino
              ? translate("nevora.mover.titulo", {
                  etapa: translate(claveEtapa(destino)),
                })
              : ""}
          </DialogTitle>
          <DialogDescription>{nombre}</DialogDescription>
        </DialogHeader>
        {motivos ? (
          <fieldset className="space-y-2" data-testid="mover-motivo">
            <legend className="etiqueta mb-2">
              {translate("nevora.resultado.motivo")}
            </legend>
            <div className="flex flex-wrap gap-2">
              {motivos.map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={motivo === m}
                  onClick={() => {
                    setMotivo(m);
                    setSinMotivo(false);
                  }}
                  className={cn(
                    "rounded-full border px-3 py-1.5 text-sm transition-colors",
                    motivo === m
                      ? "border-foreground bg-accent font-medium"
                      : "border-border bg-card hover:bg-muted",
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
            {sinMotivo ? (
              <p className="text-sm text-destructive">
                {translate("nevora.resultado.errores.falta_motivo")}
              </p>
            ) : null}
          </fieldset>
        ) : null}
        {dato ? (
          <div className="space-y-2">
            <Label htmlFor="mover_fecha">
              {translate(`nevora.mover.${dato}`)}
            </Label>
            {conHuecos ? (
              <SelectorHuecos
                valor={valor}
                cambiar={(v) => {
                  setValor(v);
                  setError(false);
                }}
              />
            ) : (
              <Input
                id="mover_fecha"
                type={
                  dato === "fecha_vuelta"
                    ? "date"
                    : esImporte
                      ? "number"
                      : "datetime-local"
                }
                {...(esImporte
                  ? { inputMode: "decimal" as const, min: "0", step: "0.01" }
                  : {})}
                value={valor}
                onChange={(e) => {
                  setValor(e.target.value);
                  setError(false);
                }}
                aria-invalid={error}
                required
              />
            )}
            {error ? (
              <p className="text-sm text-destructive">
                {translate(
                  esImporte
                    ? "nevora.mover.falta_importe"
                    : "nevora.mover.falta_fecha",
                )}
              </p>
            ) : null}
            {dato === "fecha_vuelta" ? (
              <p className="text-xs text-muted-foreground">
                {translate("nevora.mover.ayuda_vuelta")}
              </p>
            ) : null}
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancelar}>
            {translate("ra.action.cancel")}
          </Button>
          <Button type="button" onClick={mover}>
            {translate("nevora.mover.mover")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
